import Wallet from '@mui/icons-material/AccountBalanceWallet'
import CreditCard from '@mui/icons-material/CreditCard'
import Dot from '@mui/icons-material/FiberManualRecord'
import CheckGreen from 'assets/check-green.svg'
import ErrorClose from 'assets/error-close.svg'
import { IOptionsModel, WithId } from 'models/shared.models'
import { UnparsedClient } from 'models/client'
import {
  IInvoiceDraft,
  IInvoice,
  TInvoiceDraftMenuOptions,
  TInvoiceTableTabs,
  TCFDIsZIPUploadCategories,
} from 'models/invoices.models'
import RazonSocial from 'models/razonSocial'
import { INVOICE_STATUS, STATUS_PAYMENT } from '../../../constants'
import {
  INVOICE_DRAFT_MENU_OPTIONS,
  INVOICES_TABLE_TABS,
  CFDIS_ZIP_UPLOAD_CATEGORIES,
} from 'lib/enums/invoices.enums'
import {
  getFormattedCurrency,
  getFormattedDate,
  getFormattedTime,
} from 'lib/helpers/shared.helpers'

export const clearInvoicesLocalFilterValues = () => {
  ;(document.getElementById('CFDIsFilters') as HTMLFormElement)?.reset()
  ;(document.getElementById('CFDIsAdvancedFilters') as HTMLFormElement)?.reset()
}

export const invoiceDraftMenuOptionsLabelMap: Readonly<
  Record<TInvoiceDraftMenuOptions, string>
> = {
  [INVOICE_DRAFT_MENU_OPTIONS.GUARDAR]: 'Guardar',
  [INVOICE_DRAFT_MENU_OPTIONS.VISTA_PREVIA]: 'Vista previa',
  [INVOICE_DRAFT_MENU_OPTIONS.DESCARGAR_PDF]: 'Descargar como PDF',
} as const

const invoiceTableTabsLabelMap: Record<TInvoiceTableTabs, string> = {
  [INVOICES_TABLE_TABS.CFDIS]: 'CFDIs',
  [INVOICES_TABLE_TABS.PREFACTURAS]: 'Prefacturas',
} as const

export const invoiceTableTabs: WithId<IOptionsModel<TInvoiceTableTabs>>[] = [
  {
    id: INVOICES_TABLE_TABS.CFDIS,
    label: invoiceTableTabsLabelMap[INVOICES_TABLE_TABS.CFDIS],
    value: INVOICES_TABLE_TABS.CFDIS,
  },
  {
    id: INVOICES_TABLE_TABS.PREFACTURAS,
    label: invoiceTableTabsLabelMap[INVOICES_TABLE_TABS.PREFACTURAS],
    value: INVOICES_TABLE_TABS.PREFACTURAS,
  },
]

export const invoicesTableColumnHeaderLabelMap: Record<string, string> = {
  createdAt: 'Fecha',
  hour: 'Hora',
  serie: 'Serie-Folio',
  uuid: 'UUID',
  clients: 'Receptor',
  emisor: 'Emisor',
  subtotal: 'Subtotal',
  taxes: 'Impuestos',
  total: 'Total',
  movementType: 'Tipo',
  status: 'Estatus',
  art69B: 'Art. 69B',
  statusPayment: 'Pago',
  sent: 'Enviado',
} as const

export const CFDIPaymentStatusIconMap = {
  [STATUS_PAYMENT.CHARGED]: Dot,
  [STATUS_PAYMENT.PART_CHARGED]: Wallet,
  [STATUS_PAYMENT.PAID]: Dot,
  [STATUS_PAYMENT.PART_PAID]: CreditCard,
} as const

export const CFDIPaymentStatusLabelMap = {
  [STATUS_PAYMENT.CHARGED]: 'Cobrada',
  [STATUS_PAYMENT.PART_CHARGED]: 'Cobrada parcialmente',
  [STATUS_PAYMENT.PAID]: 'Pagada',
  [STATUS_PAYMENT.PART_PAID]: 'Pagada parcialmente',
} as const

export const CFDIStatusClassnameMap = {
  [INVOICE_STATUS.ACTIVE.id]: 'active',
  [INVOICE_STATUS.CANCELED.id]: 'canceled',
  [INVOICE_STATUS.PENDING.id]: 'pending',
} as const

export const CFDIStatusLabelMap = {
  [INVOICE_STATUS.ACTIVE.id]: 'Vigente',
  [INVOICE_STATUS.CANCELED.id]: 'Cancelado',
  [INVOICE_STATUS.PENDING.id]: 'Pendiente',
} as const

export const cfdisZIPUploadCategoriesTitleIconMap: Record<
  TCFDIsZIPUploadCategories,
  string
> = {
  [CFDIS_ZIP_UPLOAD_CATEGORIES.SUCCESS]: CheckGreen,
  [CFDIS_ZIP_UPLOAD_CATEGORIES.DUPLICATED]: ErrorClose,
  [CFDIS_ZIP_UPLOAD_CATEGORIES.FORMAT_ERROR]: ErrorClose,
  [CFDIS_ZIP_UPLOAD_CATEGORIES.GENERIC_ERROR]: ErrorClose,
} as const

export const cfdisZIPUploadCategoriesTitleMap: Record<
  TCFDIsZIPUploadCategories,
  string
> = {
  [CFDIS_ZIP_UPLOAD_CATEGORIES.SUCCESS]: 'se cargaron correctamente',
  [CFDIS_ZIP_UPLOAD_CATEGORIES.DUPLICATED]:
    'ya existen en tu lista de CFDIs y no se volvieron a cargar',
  [CFDIS_ZIP_UPLOAD_CATEGORIES.FORMAT_ERROR]:
    'no se cargaron debido a inconsistencias en el contenido del archivo',
  [CFDIS_ZIP_UPLOAD_CATEGORIES.GENERIC_ERROR]:
    'no pudieron ser cargados debido a un error desconocido',
} as const

export const unavailableColumnsForInvoiceDrafts: Readonly<string[]> = [
  'hour',
  'serie',
  'folio',
  'uuid',
  'status',
  'art69B',
  'statusPayment',
  'sent',
] as const

export const getFormattedEmisor = (emisor?: RazonSocial) =>
  emisor?.name ?? emisor?.rfc ?? '---'

export const getFormattedReceptor = (receptor?: UnparsedClient) =>
  receptor?.name ?? receptor?.bussinessName ?? receptor?.rfc ?? '---'

export const getFormattedSerieFolio = (
  serie?: string,
  row?: IInvoice | IInvoiceDraft
): string => {
  const { folio } = (row as IInvoice) || {}
  if (!serie && !folio) return '---'
  if (!serie && folio) return `${folio}`
  if (serie && !folio) return `${serie}`
  return `${serie}-${folio}`
}

export const getInvoiceFormattedDate = (dateString?: string): string => {
  return getFormattedDate(dateString)
}

export const getInvoiceFormattedTime = (dateString?: string): string => {
  return getFormattedTime(dateString, true)
}

export const getInvoiceFormattedCurrency = (money: number): string => {
  return getFormattedCurrency(money)
}

export const calculateInvoiceTax = (
  base: number,
  factor: string,
  value: number
) => {
  switch (factor) {
    case 'Tasa':
      return base * value
    case 'Cuota':
      return value
    default:
      return 0
  }
}
